import { emailRegex, INPUT_TYPES, isCorporateEmail } from '@/utils';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';

/**
 * Props for `Inputs`.
 */
export type InputsProps = SliceComponentProps<Content.InputsSlice> & {
  context: {
    register: UseFormRegister<FieldValues>;
    errors: FieldErrors<FieldValues>;
  };
};

/**
 * Component for "Inputs" Slices.
 */
const Inputs = ({ slice, context }: InputsProps): JSX.Element => {
  const { inputs } = slice.primary;
  const { register, errors } = context;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="grid grid-cols-2 gap-y-12 gap-x-6"
    >
      {inputs.map((input, index) => (
        <div
          key={`input-${index}`}
          className={`flex flex-col gap-y-1 ${input.full_width ? 'col-span-2' : ''}`}
        >
          <div className="flex justify-between">
            <label className="text-regular-normal">{input.label}</label>
            {input.show_tooltip && (
              <div className="relative group">
                <span className="cursor-pointer">
                  <b>Why?</b>
                </span>
                <div className="absolute bottom-full right-0 w-60 mb-2 hidden group-hover:block bg-gray-800/80 backdrop-blur-md text-white text-sm p-4 rounded-[20px] shadow-lg">
                  {input.tooltip_text}
                </div>
              </div>
            )}
          </div>
          {input.type === INPUT_TYPES.dropdown && (
            <select
              className="border border-gray-400 rounded-lg bg-white/80 h-14 p-3 outline-none text-regular-normal no-select-arrow"
              id={input.hubspot_name!}
              {...register(input.hubspot_name!, {
                required: {
                  value: input.required,
                  message: `${input.label} is required`,
                },
              })}
            >
              <option value="">Select an option</option>
              {(input.options as { text: string }[]).map((option, i) => (
                <option key={`option-${i}`} value={option.text}>
                  {option.text}
                </option>
              ))}
            </select>
          )}
          {input.type === INPUT_TYPES.textarea && (
            <textarea
              className="border border-gray-400 rounded-lg bg-white/80  p-3 outline-none text-regular-normal resize-none"
              rows={5}
              placeholder="Type your message..."
              id={input.hubspot_name!}
              {...register(input.hubspot_name!, {
                required: {
                  value: input.required,
                  message: `${input.label} is required`,
                },
              })}
            />
          )}
          {[INPUT_TYPES.text, INPUT_TYPES.email, INPUT_TYPES.phone].includes(
            input.type as INPUT_TYPES,
          ) && (
            <input
              type={input.type}
              className="border border-gray-400 rounded-lg bg-white/80 h-14 p-3 outline-none text-regular-normal"
              id={input.hubspot_name!}
              {...register(input.hubspot_name!, {
                required: {
                  value: input.required,
                  message: `${input.label} is required`,
                },
                ...(input.type === INPUT_TYPES.email && {
                  pattern: {
                    value: emailRegex,
                    message: 'Enter a valid email',
                  },
                  validate: {
                    isCorporate: (value) =>
                      isCorporateEmail(value) ||
                      'Please use a corporate email address',
                  },
                }),
              })}
            />
          )}
          {errors[input.hubspot_name!] && (
            <span className="text-error text-sm mt-1 px-3">
              {errors[input.hubspot_name!]?.message as string}
            </span>
          )}
        </div>
      ))}
    </section>
  );
};

export default Inputs;
